import { Spinner, Stack } from 'component-library';
import { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { SuspenseBoundary } from '@/components';
import { Card } from '@/components/widgets/o/Card/Card';
import { Logger } from '@/lib/logs/logger';

const ErrorFallback = ({ name }: { name?: string }) => {
  const { t } = useTranslation();

  const cardProps = {
    styles: {
      root: 'rounded-3xl p-4',
    },
  };

  return (
    <Card {...cardProps}>
      <Stack spacing={4}>
        {name && <span className='typography-body1'>{name}</span>}
        <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
      </Stack>
    </Card>
  );
};

const WithErrorBoundary = ({
  name,
  children,
  errorFallBack,
}: {
  name?: string;
  children: ReactNode;
  errorFallBack?: ReactNode;
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={() => (errorFallBack ? <>{errorFallBack}</> : <ErrorFallback name={name} />)}
      onError={(error, info) => {
        Logger.error(
          name
            ? `Plot card rendering error (name: ${name}): ${error} ${info}`
            : `Plot card rendering error: ${error} ${info}`,
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

const LoaderFallback = () => (
  <Stack centerMain>
    <Spinner size={10} />
  </Stack>
);

export const PlotCardWrapper = ({
  name,
  children,
  errorFallBack,
  loaderFallBack,
}: {
  name?: string;
  children: ReactNode;
  errorFallBack?: ReactNode;
  loaderFallBack?: ReactNode;
}) => {
  return (
    <WithErrorBoundary name={name} errorFallBack={errorFallBack}>
      <SuspenseBoundary fallback={loaderFallBack ? <>{loaderFallBack}</> : <LoaderFallback />}>
        {children}
      </SuspenseBoundary>
    </WithErrorBoundary>
  );
};
